/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { shape, func, string, object } from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import update from 'immutability-helper';

import p from 'polyglot/polyglot';

import { createUrl } from 'utils/urlHelper';

import {
  CREATE_SHORTLIST_SUCCESS,
  CREATE_SHORTLIST_FAILURE,
  createShortlist as createShortlistAction,
} from 'actions/shortlist';

import Button from 'components/Button/Button';
import BlackTieIcon from 'components/BlackTieIcon/BlackTieIcon';
import Select from 'components/Form/Input/Select/Select';

import styles from './shortlist-create.scss';

/**
 * This is the ShortlistCreate component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export class ShortlistCreate extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    auth: shape({
      token: string.isRequired,
    }).isRequired,
    bootstrap: shape({
      constants: object.isRequired,
    }).isRequired,
    history: object.isRequired,
    createShortlist: func.isRequired,
  };

  state = {
    skill: '',
    skills: {},
  };

  onChangeSkill = event => {
    const { value: skill } = event.target;

    this.setState({ skill });
  };

  onChangeTotal = (event, skill) => {
    const { skills } = this.state;
    const { value: total } = event.target;

    this.setState({
      skills: update(skills, {
        [skill]: { $set: parseInt(total, 10) },
      }),
    });
  };

  onAdd = event => {
    event.preventDefault();

    const { skill, skills } = this.state;

    if (!skill) return;

    const exists = !!skills[skill];

    if (!exists) {
      this.setState({
        skill: '',
        skills: update(skills, {
          $merge: { [skill]: 1 },
        }),
      });
    } else {
      const total = skills[skill] + 1;

      this.setState({
        skills: update(skills, {
          [skill]: { $set: total },
        }),
      });
    }
  };

  onSubmit = event => {
    event.preventDefault();

    const {
      auth: { token },
      createShortlist,
      history,
    } = this.props;
    const { skills } = this.state;
    const total = Object.keys(skills).length;

    if (total < 1) return;

    const needs = Object.keys(skills).map(skill => ({
      skill,
      count: skills[skill],
    }));

    createShortlist(token, { needs }).then(({ type, payload }) => {
      if (type === CREATE_SHORTLIST_SUCCESS) {
        history.push(createUrl('shortlist', payload.shortlist_id));
      }

      if (type === CREATE_SHORTLIST_FAILURE) {
        console.log('ERROR', payload); // @TODO: Handle error.
      }
    });
  };

  renderItems = () => {
    const { skills } = this.state;

    return Object.keys(skills).map(skill => (
      <div className={styles.skill} key={skill}>
        <p>{p.t(`shortlist.create.types.${skill.toLowerCase()}`)}</p>
        <input
          type="number"
          min="1"
          className={styles.input}
          value={skills[skill]}
          onChange={e => this.onChangeTotal(e, skill)}
        />
      </div>
    ));
  };

  /**
   * Render ShortlistCreate.
   *
   * @return {Object}
   */
  render() {
    const { skill, skills } = this.state;
    const {
      bootstrap: {
        constants: { position_skills },
      },
    } = this.props;

    const total = Object.keys(skills).length;

    return (
      <section className={styles.container}>
        <div className={styles.row}>
          <header className={styles.header}>
            <h1 className={styles.title}>{p.t('shortlist.create.title')}</h1>

            <div className={styles.select}>
              <Select
                name="hero-select"
                options={position_skills}
                onChange={this.onChangeSkill}
                value={skill}
                placeholder={p.t('shortlist.create.placeholder')}
                required
              />

              <Button icon className={styles.addButton} onClick={this.onAdd}>
                <BlackTieIcon
                  icon="plus"
                  weight="btb"
                  className={styles.icon}
                />
              </Button>
            </div>

            <p className={styles.tagline}>{p.t('shortlist.create.tagline')}</p>
          </header>

          <div className={styles.items}>{this.renderItems()}</div>

          <footer className={styles.footer}>
            <Button onClick={this.onSubmit} disabled={total < 1}>
              {p.t('general.next')}
            </Button>
          </footer>
        </div>
      </section>
    );
  }
}

export default withRouter(
  connect(
    ({ auth, app, bootstrap }) => ({ auth, app, bootstrap }),
    {
      createShortlist: createShortlistAction,
    }
  )(ShortlistCreate)
);
